import { combineEpics, ofType } from "redux-observable";
import { of, from, Observable, mergeMap, catchError } from "rxjs";
import { AnyAction } from "redux";
import { RootStateOrAny } from "react-redux";

import { fetchPositionsUsecase } from "../../core/usecases/interactors/position/fetch-positions";
import { errorString } from "../../utils/strings";
import { addPositionUseCase } from "../../core/usecases/interactors/position/add-position";
import {
  fetchPositionSuccess,
  fetchPositionsFailure,
  updatePositionSuccess,
  updatePositionFailure,
  fetchPositionByIdSuccess,
  addPositionSuccess,
  addPositionFailure,
} from "./action";
import {
  FETCH_POSITIONS_FAILURE,
  FETCH_POSITIONS_REQUEST,
  FETCH_POSITION_REQUEST,
  ADD_POSITION_REQUEST,
  UPDATE_POSITION_REQUEST,
} from "./actionType";
import {
  updatePositionUsecase,
  fetchPositionUsecase,
} from "../../core/usecases/interactors/position";
import { failedRequest, succeededRequest } from "../request/action";
import { data as constants } from "../../constants";

export const fetchPositionsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    positionInteractor: { fetchPositions },
  }: { positionInteractor: { fetchPositions: fetchPositionsUsecase } }
): Observable<
  | ReturnType<typeof fetchPositionSuccess>
  | ReturnType<typeof fetchPositionsFailure>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_POSITIONS_REQUEST),
    mergeMap(({ payload: { limit, where } }) => {
      return from(fetchPositions(limit, where)).pipe(
        mergeMap((response) => {
          return of(
            fetchPositionSuccess(response),
            succeededRequest(FETCH_POSITIONS_REQUEST)
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(constants.commonNetworkError) &&
            window.location.reload();
          return of(
            fetchPositionsFailure(errorMessage),
            failedRequest(FETCH_POSITIONS_FAILURE, errorMessage)
          );
        })
      );
    })
  );

export const addPositionEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    positionInteractor: { addPosition },
  }: { positionInteractor: { addPosition: addPositionUseCase } }
): Observable<
  | ReturnType<typeof addPositionSuccess>
  | ReturnType<typeof addPositionFailure>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(ADD_POSITION_REQUEST),
    mergeMap(({ payload: { position } }) => {
      return from(addPosition(position)).pipe(
        mergeMap(() => {
          return of(
            addPositionSuccess(),
            succeededRequest(ADD_POSITION_REQUEST, position)
          );
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(
            addPositionFailure(errorMessage),
            failedRequest(ADD_POSITION_REQUEST, errorMessage)
          );
        })
      );
    })
  );

export const fetchPositionEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    positionInteractor: { fetchPosition },
  }: { positionInteractor: { fetchPosition: fetchPositionUsecase } }
): Observable<
  | ReturnType<typeof fetchPositionByIdSuccess>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_POSITION_REQUEST),
    mergeMap(({ type, payload: { id } }) => {
      return from(fetchPosition(id)).pipe(
        mergeMap((response) => {
          return of(
            succeededRequest(FETCH_POSITION_REQUEST, response),
            fetchPositionByIdSuccess(response)
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(
            "Failed to get document because the client is offline."
          ) && window.location.reload();
          return of(failedRequest(type, errorMessage));
        })
      );
    })
  );

export const updatePositionEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    positionInteractor,
  }: { positionInteractor: { updatePosition: updatePositionUsecase } }
): Observable<
  | ReturnType<typeof updatePositionSuccess>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
  | ReturnType<typeof updatePositionFailure>
> =>
  action$.pipe(
    ofType(UPDATE_POSITION_REQUEST),
    mergeMap(({ type, payload: { id, data } }) => {
      return from(positionInteractor.updatePosition(id, data)).pipe(
        mergeMap(() => {
          return of(
            succeededRequest(UPDATE_POSITION_REQUEST, data),
            updatePositionSuccess()
          );
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(
            failedRequest(type, errorMessage),
            updatePositionFailure(errorMessage)
          );
        })
      );
    })
  );

export default combineEpics(
  fetchPositionsEpic,
  // @ts-ignore
  addPositionEpic,
  fetchPositionEpic,
  updatePositionEpic
);
